// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { BadgeModel } from "@insite/client-framework/Types/ApiModels";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Badge from "@werner/MobiusOverrides/Badge/Badge";
import * as React from "react";
import { useState } from "react";
import { css } from "styled-components";

interface Props {
    badgeList: BadgeModel[];
    maxBadges: number;
    extendedStyles?: ProductTextBadgesStyles;
}

export interface ProductTextBadgesStyles {
    container?: InjectableCss;
    badge?: InjectableCss;
    badgeText?: TypographyPresentationProps;
    // XNG-Change
    textBadgeWrapperStyles?: InjectableCss;
}

export const productTextBadgesStyles: ProductTextBadgesStyles = {
    badge: {
        css: css`
            margin-top: 5px;
        `,
    },
    badgeText: {
        weight: "bold",
        size: 15,
        css: css`
            width: 100%;
            margin: auto;
            ${wrapInContainerStyles}
        `,
    },
};

const ProductTextBadges = ({ badgeList, maxBadges, extendedStyles }: Props) => {
    const [styles] = useState(() => mergeToNew(productTextBadgesStyles, extendedStyles));

    return (
        <StyledWrapper {...styles.container} data-test-selector="textBadges">
            {badgeList.slice(0, maxBadges).map(badge => (
                <StyledWrapper key={badge.id} {...styles.badge}>
                    <Badge
                        type={badge.badgeType}
                        value={badge.displayText}
                        badgeTextColor={badge.textColorHexCode}
                        badgeBackgroundColor={badge.badgeColorHexCode}
                        badgeTextRounding={badge.badgeStyle === "Round" ? 20 : 0}
                        textBadgeCss={styles.badgeText}
                        {...styles.badge}
                        // XNG-Change
                        textBadgeWrapperCss={styles.textBadgeWrapperStyles}
                    />
                </StyledWrapper>
            ))}
        </StyledWrapper>
    );
};

export default ProductTextBadges;
